import React, { useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  CardMembership as MembershipIcon,
  TrendingUp as TrendingUpIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Autorenew as AutorenewIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { statsApi } from '../services/api';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  subtitle?: string;
  helpText?: string;
}

function StatCard({ title, value, icon, color = '#1976d2', subtitle, helpText }: StatCardProps) {
  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            backgroundColor: color + '15',
            color: color,
            mr: 2,
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6" component="div" sx={{ fontSize: '0.95rem', flexGrow: 1 }}>
          {title}
        </Typography>
        {helpText && (
          <Tooltip title={helpText} arrow placement="top">
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Typography variant="h4" component="div" sx={{ mb: 1, fontWeight: 600 }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
}

export default function SubscriptionsAnalytics() {
  const [days, setDays] = useState(30);
  const [includeBlocked, setIncludeBlocked] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['subscriptionsAnalytics', days, includeBlocked],
    queryFn: () => statsApi.getSubscriptionsAnalytics(days, includeBlocked),
    refetchInterval: 60000, // Обновление каждую минуту
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Ошибка загрузки статистики подписок: {(error as Error).message}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Нет данных для отображения
      </Alert>
    );
  }

  return (
    <Box>
      {/* Информация о расчётах */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Как считаются метрики:</strong>
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Всего подписок</strong> - уникальные пользователи с успешными платежами (payments.status = 'completed')<br />
          • <strong>Активные подписки</strong> - пользователи с subscription = 'True' и end_date {'>'} сейчас<br />
          • <strong>Истёкшие</strong> - пользователи, которые платили, но сейчас подписка неактивна<br />
          • <strong>Новые подписки по дням</strong> - считаются по дате первого платежа каждого пользователя<br />
          • <strong>Типы подписок и автопродление</strong> - только для активных подписчиков<br />
          • <strong>Источники</strong> - для всех пользователей с платежами (не только активных)
        </Typography>
      </Alert>

      {/* Фильтры */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Период</InputLabel>
            <Select
              value={days}
              label="Период"
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <MenuItem value={7}>7 дней</MenuItem>
              <MenuItem value={14}>14 дней</MenuItem>
              <MenuItem value={30}>30 дней</MenuItem>
              <MenuItem value={60}>60 дней</MenuItem>
              <MenuItem value={90}>90 дней</MenuItem>
              <MenuItem value={180}>180 дней</MenuItem>
              <MenuItem value={365}>365 дней</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControlLabel
            control={
              <Switch
                checked={includeBlocked}
                onChange={(e) => setIncludeBlocked(e.target.checked)}
              />
            }
            label="Включить заблокированных"
          />
        </Grid>
      </Grid>

      {/* Общая статистика */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Общая статистика
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Всего подписок"
            value={data.summary.total_subscriptions_ever}
            icon={<MembershipIcon />}
            color="#1976d2"
            subtitle="За всё время"
            helpText="Уникальные пользователи, которые хотя бы раз оплатили подписку (по таблице payments)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Активные подписки"
            value={data.summary.active_subscriptions}
            icon={<CheckCircleIcon />}
            color="#2e7d32"
            subtitle="Сейчас активны"
            helpText="Пользователи с subscription='True' и end_date > сейчас"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Истёкшие подписки"
            value={data.summary.expired_subscriptions}
            icon={<CancelIcon />}
            color="#d32f2f"
            subtitle="Закончились"
            helpText="Пользователи, которые платили, но сейчас подписка неактивна"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Конверсия в подписчики"
            value={`${data.summary.subscription_conversion_rate}%`}
            icon={<TrendingUpIcon />}
            color="#ed6c02"
            subtitle="% пользователей с подпиской"
            helpText="(Пользователи с платежами / Всего пользователей) × 100%"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Автопродление отключено"
            value={data.autopay.disabled}
            icon={<AutorenewIcon />}
            color="#f57c00"
            subtitle={`${data.autopay.disabled_rate}% от активных`}
            helpText="Считается только для пользователей с активной подпиской"
          />
        </Grid>
      </Grid>

      {/* Графики динамики */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* График новых подписок */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Новые подписки по дням
                </Typography>
                <Tooltip title="Считается по дате первого платежа каждого пользователя" arrow>
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={data.timeline.new_subscriptions}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => {
                      const d = new Date(date);
                      return `${d.getDate()}.${d.getMonth() + 1}`;
                    }}
                  />
                  <YAxis />
                  <ChartTooltip
                    labelFormatter={(date) => {
                      const d = new Date(date);
                      return d.toLocaleDateString('ru-RU');
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#1976d2"
                    fill="#1976d2"
                    fillOpacity={0.3}
                    name="Новых подписок"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* График активных подписок */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Динамика активных подписок
                </Typography>
                <Tooltip title="Для каждого дня: пользователи с subscription='True', start_date <= день, end_date > день" arrow>
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.timeline.active_subscriptions}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => {
                      const d = new Date(date);
                      return `${d.getDate()}.${d.getMonth() + 1}`;
                    }}
                  />
                  <YAxis />
                  <ChartTooltip
                    labelFormatter={(date) => {
                      const d = new Date(date);
                      return d.toLocaleDateString('ru-RU');
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#2e7d32"
                    strokeWidth={2}
                    name="Активных подписок"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Распределения */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Типы подписок */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Типы подписок (среди активных)
                </Typography>
                <Tooltip title="Только пользователи с активной подпиской" arrow>
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              {data.subscription_types.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={data.subscription_types}
                      dataKey="count"
                      nameKey="type"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ type, count }) => `${type}: ${count}`}
                    >
                      {data.subscription_types.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Alert severity="info">Нет данных о типах подписок</Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Автопродление */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Статус автопродления (среди активных)
                </Typography>
                <Tooltip title="Только пользователи с активной подпиской" arrow>
                  <IconButton size="small">
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Включено', value: data.autopay.enabled },
                      { name: 'Отключено', value: data.autopay.disabled },
                    ]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    <Cell fill="#2e7d32" />
                    <Cell fill="#f57c00" />
                  </Pie>
                  <ChartTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ mt: 2 }}>
                <Alert severity="warning">
                  {data.autopay.disabled_rate}% активных подписчиков отключили автопродление
                </Alert>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Источники подписчиков */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Источники привлечения подписчиков
            </Typography>
            <Tooltip title="Все пользователи с платежами (не только активные), группированные по полю users.source" arrow>
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {data.sources.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={data.sources}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="source" />
                <YAxis />
                <ChartTooltip />
                <Legend />
                <Bar dataKey="count" fill="#1976d2" name="Количество подписчиков" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Alert severity="info">Нет данных об источниках</Alert>
          )}
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {data.sources.map((source, index) => (
              <Chip
                key={index}
                label={`${source.source}: ${source.count} (${source.percentage}%)`}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
